import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Workbox } from 'workbox-window';
import { startSyncWorker } from './utils/posSyncWorker';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// ─── Service worker registration ────────────────────────────────────────
// Production-only on purpose. Registering an SW in dev (`npm start`) causes
// stale-asset issues with hot reload — CRA recommends prod-only registration.
//
// The Workbox wrapper gives us:
//   - clean lifecycle events ('waiting' fires when a new SW is installed but
//     blocked by the current one still controlling the page)
//   - messageSkipWaiting() to tell the waiting SW to activate
//
// On 'waiting' we dispatch a custom event the <UpdateBanner /> listens for.
// User clicks "Reload now" → SKIP_WAITING → new SW activates → page reloads.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const wb = new Workbox('/service-worker.js');
    window.__AMAL_WB__ = wb; // expose for <UpdateBanner /> to send SKIP_WAITING
    const loadedAt = Date.now();
    const tabIsFresh = () => Date.now() - loadedAt < 60000;

    // Update found at startup → apply silently (user just arrived, nothing to
    // lose). Update found mid-session → banner only; NEVER interrupt work
    // (a half-typed invoice or open POS cart must survive).
    wb.addEventListener('waiting', () => {
      if (tabIsFresh()) {
        wb.messageSkipWaiting();
      } else {
        window.dispatchEvent(new CustomEvent('amal:update-available'));
      }
    });

    // New SW took control. isUpdate guards against first-ever installs
    // (clients.claim fires 'controlling' then too — reloading every brand-new
    // visitor would be a loop hazard). skipWaiting is origin-global, so this
    // fires in EVERY open tab: only reload tabs that can't have work in
    // progress (just opened, or not being looked at).
    wb.addEventListener('controlling', (event) => {
      if (!event.isUpdate) return;
      if (tabIsFresh() || document.visibilityState === 'hidden') {
        window.location.reload();
      } else {
        window.dispatchEvent(new CustomEvent('amal:update-available'));
      }
    });

    wb.register().catch(err => console.warn('SW registration failed:', err));

    // Long-lived tabs (POS tills stay open for days) never trigger the
    // browser's natural SW update check — poll for updates ourselves.
    let lastCheck = Date.now();
    const checkForUpdate = () => {
      if (Date.now() - lastCheck < 5 * 60 * 1000) return; // ≥5 min between checks
      lastCheck = Date.now();
      wb.update().catch(() => {});
    };
    setInterval(checkForUpdate, 60 * 60 * 1000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    });
    window.addEventListener('online', checkForUpdate);
  });
}

// Boot watchdog handshake (see inline script in public/index.html): mark the
// app as successfully booted so the watchdog doesn't reload, and reset its
// retry counter for the next cold start.
window.__APP_BOOTED__ = true;
if (window.__AMAL_BOOT_TIMER__) clearTimeout(window.__AMAL_BOOT_TIMER__);
try { sessionStorage.removeItem('amal_boot_retry'); } catch { /* private mode */ }

// Start the POS sync worker so any outbox-queued sales from a previous
// offline session get pushed as soon as the user is back online. The worker
// idle-polls every 30s and immediately drains on 'online' events. Safe to
// run in dev too — no-op if the outbox is empty.
startSyncWorker();
